import { useEffect, useState } from "react";
import { AdminLayout } from "./Admin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, Download } from "lucide-react";

type Row = {
  id: string; kind: string; title: string; recipient: string | null;
  email_status: string; email_error: string | null;
  in_app_status: string; in_app_error: string | null;
  entity_type: string | null; entity_id: string | null;
  created_at: string; metadata: any;
};

const statusVariant = (s: string) =>
  s === "sent" ? "default" : s === "failed" ? "destructive" : "secondary";

const AdminNotificationDeliveryLog = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState<string>("");

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("notification_delivery_log" as any)
      .select("*").order("created_at", { ascending: false }).limit(300);
    setRows(((data as any[]) ?? []) as Row[]);
    setLoading(false);
  };
  useEffect(() => {
    load();
    // delivery log removed from realtime publication for privacy; poll.
    const id = window.setInterval(() => { load(); }, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { window.clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, []);

  const kinds = Array.from(new Set(rows.map((r) => r.kind))).sort();
  const filtered = rows.filter((r) =>
    (!kindFilter || r.kind === kindFilter) &&
    (!q || r.title.toLowerCase().includes(q.toLowerCase()) || (r.recipient ?? "").toLowerCase().includes(q.toLowerCase()) || (r.entity_id ?? "").includes(q)),
  );

  const exportCsv = () => {
    const head = ["created_at","kind","title","recipient","email_status","email_error","in_app_status","in_app_error","entity_type","entity_id"];
    const lines = [head.join(",")].concat(filtered.map((r) => [
      r.created_at, r.kind, r.title, r.recipient ?? "", r.email_status, r.email_error ?? "",
      r.in_app_status, r.in_app_error ?? "", r.entity_type ?? "", r.entity_id ?? "",
    ].map((v) => `"${String(v ?? "").replace(/"/g,'""')}"`).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `notification-deliveries-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="NOTIFICATION DELIVERIES">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Input placeholder="Search title, recipient, entity id…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="bg-background border border-border px-3 py-2 text-sm">
          <option value="">All events</option>
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={load} className="gap-2"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        <Button variant="outline" size="sm" onClick={exportCsv} className="gap-2"><Download className="w-4 h-4" /> CSV</Button>
        <span className="ml-auto text-xs font-mono text-muted-foreground">{filtered.length} of {rows.length}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground font-body text-sm">No deliveries yet.</p>
      ) : (
        <div className="bg-panel border border-border overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="border-b border-border">
              <tr className="text-left font-mono uppercase text-xs text-muted-foreground">
                <th className="p-3">Time</th><th className="p-3">Event</th><th className="p-3">Title</th>
                <th className="p-3">Recipient</th><th className="p-3">Email</th><th className="p-3">In-app</th>
                <th className="p-3">Entity</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 align-top">
                  <td className="p-3 font-mono text-xs">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3"><Badge variant="outline">{r.kind}</Badge></td>
                  <td className="p-3">{r.title}</td>
                  <td className="p-3 text-xs">{r.recipient ?? "—"}</td>
                  <td className="p-3">
                    <Badge variant={statusVariant(r.email_status) as any}>{r.email_status}</Badge>
                    {r.email_error && <div className="text-[10px] text-destructive font-mono mt-1 max-w-xs truncate" title={r.email_error}>{r.email_error}</div>}
                  </td>
                  <td className="p-3">
                    <Badge variant={statusVariant(r.in_app_status) as any}>{r.in_app_status}</Badge>
                    {r.in_app_error && <div className="text-[10px] text-destructive font-mono mt-1 max-w-xs truncate" title={r.in_app_error}>{r.in_app_error}</div>}
                  </td>
                  <td className="p-3 text-[11px] font-mono text-muted-foreground">
                    {r.entity_type ? `${r.entity_type}/${(r.entity_id ?? "").slice(0, 8)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminNotificationDeliveryLog;